import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { TransferStore } from '../TransferStore'
import { Currency } from '../../../../../components/Currency'
import { CurrencyInput } from '../../../../../components/CurrencyInput'
import {
  API,
  TransferProduct,
  CreateTransferBatch,
  IBatchResponseWithStatus,
  TransferStatus,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { BatchStatusToggleTd } from '../../../../../components/BatchStatusToggleTd'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { TransferBatchSection } from './TransferBatch/TransferBatchSection'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  transferProduct: TransferProduct
  transferStore: TransferStore
  canEdit: boolean
  index: number
  shouldLotColumnExist: boolean
}

interface State {
  isBatchClicked: boolean
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class TransferRow extends React.Component<Props, State> {
  companyId = this.props.currentCompanyStore!.company!.id

  constructor(props: Props) {
    super(props)
    this.state = {
      isBatchClicked: false,
    }
  }

  componentDidMount = async () => {
    if (this.props.transferProduct.isBatchTracked) {
      await this.createBatchOptions()
    }
  }

  createBatchOptions = async () => {
    const { transferProduct } = this.props
    const transferProductId = transferProduct.id

    if (!transferProductId) {
      return
    }

    const { fromLocationId, toLocationId } = this.props.transferStore.editTransfer

    const fromBatches = await this.loadBatches(fromLocationId, transferProductId)
    if (!fromBatches) {
      return
    }

    const toBatches = await this.loadBatches(toLocationId, transferProductId)

    const batchOptions: CreateTransferBatch[] = []

    for (const fromBatch of fromBatches) {
      const batchOption = this.createTransferBatchOption(fromBatch, toBatches)
      batchOptions.push(batchOption)

      const matchingBatch = transferProduct.batches.find(
        (batch) => batch.batchNumber === batchOption.batchNumber,
      )

      if (matchingBatch) {
        matchingBatch.setFromLocationInventory(batchOption.fromLocationInventory || 0)
        matchingBatch.setToLocationInventory(batchOption.toLocationInventory || 0)
      }
    }

    transferProduct.setBatchOptions(batchOptions)
  }

  createTransferBatchOption = (
    fromBatch: IBatchResponseWithStatus,
    toBatches: IBatchResponseWithStatus[] | undefined,
  ): CreateTransferBatch => {
    const transferBatch = new CreateTransferBatch({
      batchId: fromBatch.id,
      batchNumber: fromBatch.batchNumber,
      quantityRemaining: fromBatch.quantityRemaining,
      quantity: 0,
    })

    transferBatch.setFromLocationInventory(fromBatch.quantityRemaining)

    if (toBatches) {
      const matchingBatch = toBatches.find(
        (toBatch) => toBatch.batchNumber === fromBatch.batchNumber,
      )
      const toLocationInventory = matchingBatch ? matchingBatch.quantityRemaining : 0

      transferBatch.setToLocationInventory(toLocationInventory)
    } else {
      transferBatch.setToLocationInventory(0)
    }

    return transferBatch
  }

  loadBatches = async (
    locationId: number | undefined,
    productId: string,
  ): Promise<IBatchResponseWithStatus[] | undefined> => {
    if (!locationId) {
      return
    }
    return API.getAvailableBatches(this.companyId, locationId, productId, true)
  }

  toggleIsBatchClicked = () => this.setState({ isBatchClicked: !this.state.isBatchClicked })

  loadExistingInventory() {
    const { transferProduct } = this.props
    const productId = transferProduct.id
    const fromLocationId = this.props.transferStore.editTransfer.fromLocationId
    const toLocationId = this.props.transferStore.editTransfer.toLocationId
    if (productId) {
      if (fromLocationId) {
        API.getInventory(this.companyId, fromLocationId, { productIds: [productId] }).then(
          (inventory) => {
            if (inventory.length > 0) {
              transferProduct.fromLocationInventory = inventory[0].quantity
            }
          },
        )
      }
      if (toLocationId) {
        API.getInventory(this.companyId, toLocationId, { productIds: [productId] }).then(
          (inventory) => {
            if (inventory.length > 0) {
              transferProduct.toLocationInventory = inventory[0].quantity
            }
          },
        )
      }
    }
  }

  get shouldProductQuantityDisabled(): boolean {
    const { transferProduct } = this.props

    if (transferProduct.isBatchTracked) {
      return transferProduct.batchOptions.length > 0
    } else {
      return false
    }
  }

  render() {
    const canEditTransfers = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_TRANSFERS,
      this.companyId,
    )
    const isEditable = this.props.transferStore.isEditable
    const { transferProduct, transferStore } = this.props

    let fromLocationStockSection: JSX.Element
    if (transferProduct.fromLocationInventory === undefined) {
      fromLocationStockSection = (
        <a className='d-print-none' onClick={() => this.loadExistingInventory()}>
          Show
        </a>
      )
    } else {
      fromLocationStockSection = (
        <span>
          {transferProduct.fromLocationInventory} {transferProduct.unit}s
        </span>
      )
    }

    let toLocationStockSection: JSX.Element
    if (transferProduct.toLocationInventory === undefined) {
      toLocationStockSection = toLocationStockSection = <span>-</span>
    } else {
      toLocationStockSection = (
        <span>
          {transferProduct.toLocationInventory} {transferProduct.unit}s
        </span>
      )
    }

    return (
      <>
        <tr>
          <td>{transferProduct.name}</td>
          <td>{transferProduct.sku}</td>
          <td>{fromLocationStockSection}</td>
          <td>{toLocationStockSection}</td>
          <td style={{ minWidth: 120, maxWidth: 120 }}>
            {isEditable ? (
              <div className='input-group input-group-sm'>
                <input
                  className={
                    'form-control form-control-sm ' +
                    (transferProduct.quantity === null ? 'border-danger' : '')
                  }
                  type='number'
                  value={transferProduct.quantity === null ? undefined : transferProduct.quantity}
                  disabled={!this.props.canEdit || this.shouldProductQuantityDisabled}
                  onChange={(e) => {
                    transferProduct.setQuantity(parseFloat(e.target.value))
                    this.loadExistingInventory()
                    this.props.transferStore.hasUnsavedChanges = true
                  }}
                  min={1}
                />
                <div className='input-group-append'>
                  <div className='input-group-text'>{transferProduct.unit || 'unit'}s</div>
                </div>
              </div>
            ) : (
              `${transferProduct.quantity} ${transferProduct.unit}s`
            )}
          </td>
          {this.props.shouldLotColumnExist &&
            (transferProduct.isBatchTracked &&
            transferProduct.batchOptions &&
            transferProduct.batchOptions.length > 0 ? (
              <BatchStatusToggleTd
                isBatchClicked={this.state.isBatchClicked}
                toggleIsBatchClicked={this.toggleIsBatchClicked}
                totalCountedQuantity={transferProduct.quantity}
                totalExpectedQuantity={transferProduct.sumBatchQuantity}
              />
            ) : (
              <td style={{ width: '5em', whiteSpace: 'nowrap' }} className='text-center'>
                -
              </td>
            ))}
          <td style={{ maxWidth: 100 }}>
            {canEditTransfers && isEditable ? (
              <CurrencyInput
                className='form-control form-control-sm'
                centValue={transferProduct.costPerUnit}
                onChange={(newValue) => {
                  transferProduct.setCostPerUnit(newValue)
                  this.props.transferStore.hasUnsavedChanges = true
                }}
              />
            ) : (
              <Currency centValue={transferProduct.costPerUnit} />
            )}
          </td>
          <td style={{ maxWidth: 100 }}>
            {canEditTransfers && isEditable ? (
              <CurrencyInput
                className='form-control form-control-sm'
                centValue={transferProduct.total}
                onChange={(newValue) => {
                  transferProduct.setTotal(newValue)
                  this.props.transferStore.hasUnsavedChanges = true
                }}
              />
            ) : (
              <Currency centValue={transferProduct.total} />
            )}
          </td>
          <td>
            {canEditTransfers &&
            this.props.transferStore.editTransfer.status !== TransferStatus.COMPLETED ? (
              <input
                className='form-control form-control-sm'
                value={transferProduct.notes || ''}
                onChange={(e) => {
                  transferProduct.notes = e.target.value
                  this.props.transferStore.hasUnsavedChanges = true
                }}
              />
            ) : (
              transferProduct.notes
            )}
          </td>
          <td>
            {canEditTransfers && isEditable && (
              <a
                className='d-print-none'
                onClick={() => {
                  this.props.transferStore.editTransfer.removeProduct(this.props.index)
                  this.props.transferStore.hasUnsavedChanges = true
                }}
              >
                <span className='ml-4 fa fa-times' />
              </a>
            )}
          </td>
        </tr>

        {this.state.isBatchClicked && transferProduct.batchOptions.length > 0 && (
          <tr>
            <td colSpan={9}>
              <TransferBatchSection
                transferProduct={transferProduct}
                transferStore={transferStore}
                canEditTransfers={canEditTransfers}
                isEditable={isEditable}
              />
            </td>
          </tr>
        )}
      </>
    )
  }
}
